import type {RefAttributes, ReactNode} from 'react'
import {createContext, use, useRef, useCallback, useEffect, useState} from 'react'
import {ExpandIcon} from '../../media/icons/expand-icon/expand-icon'
import Styled from './tree.styled'
import {
    type TreeProps as AriaTreeProps,
    type TreeItemProps as AriaTreeItemProps,
    type TreeItemContentProps as AriaTreeItemContentProps,
    type TreeItemContentRenderProps,
    type Selection,
} from 'react-aria-components'
import {
    hasNodeWithTextValue,
    selectionContainsAllChildren,
    selectionContainsSomeChildren,
} from './util'
import type {Collection, Node} from 'react-stately'
import type {Key} from 'react-stately'
import {applyGranularSelectionRules} from './granular-selection-helpers'

type TreeProps<T> = AriaTreeProps<T> &
    RefAttributes<HTMLDivElement> & {
        filter?: string
        isDisabled?: boolean
        granularMode?: boolean
        defaultSelectedKeys?: 'all' | Iterable<Key>
        renderExpandIcon?: (isExpanded: boolean) => ReactNode
    }
export type TreeItemProps<T> = Omit<AriaTreeItemProps<T>, 'children'> &
    RefAttributes<HTMLDivElement> & {
        children?: ReactNode
        renderItem?: (props: TreeItemProps<T>) => ReactNode
    }
type TreeItemContentProps = AriaTreeItemContentProps & RefAttributes<HTMLDivElement>

const FilterContext = createContext<string | undefined>(undefined)

export function Tree<T extends object>({
    children,
    filter,
    granularMode = false,
    renderExpandIcon,
    onSelectionChange,
    selectedKeys,
    defaultSelectedKeys,
    ...props
}: TreeProps<T>) {
    const normalizeSelection = useCallback((keys: typeof selectedKeys): Selection | undefined => {
        if (keys === undefined) {
            return undefined
        }
        if (keys === 'all') {
            return 'all'
        }
        if (keys instanceof Set) {
            return keys
        }
        return new Set(keys)
    }, [])

    const [internalSelectedKeys, setInternalSelectedKeys] = useState<Selection | undefined>(() =>
        normalizeSelection(selectedKeys !== undefined ? selectedKeys : defaultSelectedKeys),
    )
    const collectionRef = useRef<Collection<Node<T>> | null>(null)

    useEffect(() => {
        if (selectedKeys !== undefined) {
            setInternalSelectedKeys(normalizeSelection(selectedKeys))
        }
    }, [selectedKeys, normalizeSelection])

    const handleSelectionChange = useCallback(
        (selection: Selection) => {
            let modifiedSelection = selection

            if (
                granularMode &&
                selection !== 'all' &&
                selection instanceof Set &&
                collectionRef.current
            ) {
                const currentSelection = internalSelectedKeys
                const currentSelectionSet: Set<Key> =
                    currentSelection === undefined || currentSelection === 'all'
                        ? new Set<Key>()
                        : new Set(currentSelection as Iterable<Key>)

                const newSelectionSet = new Set(selection)

                const addedKeys = Array.from(newSelectionSet).filter(
                    (key) => !currentSelectionSet.has(key),
                )
                const removedKeys = Array.from(currentSelectionSet).filter(
                    (key) => !newSelectionSet.has(key),
                )

                if (addedKeys.includes('all')) {
                    modifiedSelection = selection
                } else if (removedKeys.includes('all')) {
                    modifiedSelection = new Set()
                } else if (addedKeys.length === 0 && removedKeys.length > 0) {
                    modifiedSelection = selection
                } else if (addedKeys.length > 0) {
                    const modifiedSet = new Set(newSelectionSet)
                    applyGranularSelectionRules(
                        modifiedSet,
                        addedKeys,
                        currentSelectionSet,
                        collectionRef.current,
                    )
                    modifiedSelection = modifiedSet
                }
            }

            if (selectedKeys === undefined) {
                setInternalSelectedKeys(modifiedSelection)
            }
            onSelectionChange?.(modifiedSelection)
        },
        [granularMode, onSelectionChange, internalSelectedKeys, selectedKeys],
    )

    const selectedKeysForTree = granularMode ? internalSelectedKeys : selectedKeys

    return (
        <FilterContext value={filter}>
            <TreeStateContext.Provider value={{collectionRef, renderExpandIcon}}>
                <Styled.Tree
                    {...props}
                    data-disabled={props.isDisabled ? true : undefined}
                    selectedKeys={selectedKeysForTree}
                    onSelectionChange={handleSelectionChange}
                >
                    {children}
                </Styled.Tree>
            </TreeStateContext.Provider>
        </FilterContext>
    )
}

type TreeStateContextValue<T> = {
    collectionRef: React.RefObject<Collection<Node<T>> | null>
    renderExpandIcon?: (isExpanded: boolean) => ReactNode
}

const TreeStateContext = createContext<TreeStateContextValue<unknown> | null>(null)

export function TreeItem<T extends object>({children, renderItem, ...props}: TreeItemProps<T>) {
    const {textValue} = props
    return (
        <Styled.TreeItem {...props}>
            <TreeItemContent>{renderItem ? renderItem(props) : textValue}</TreeItemContent>
            {children}
        </Styled.TreeItem>
    )
}

function TreeItemContent({
    children,
    ...props
}: Omit<TreeItemContentProps, 'children'> & {children?: React.ReactNode}) {
    const filter = use(FilterContext)
    const stateContext = use(TreeStateContext)

    return (
        <Styled.TreeItemContent {...props}>
            {({
                hasChildItems,
                selectionBehavior,
                selectionMode,
                isExpanded,
                state,
                id,
            }: TreeItemContentRenderProps) => {
                const isMultiple = selectionMode === 'multiple'
                const col = state.collection

                if (stateContext) {
                    stateContext.collectionRef.current = col
                }

                const node = id ? col.getItem(id) : null
                const isValid = node && col
                const hidden = filter && isValid && !hasNodeWithTextValue(node, col, filter)
                const allChildrenSelected =
                    isValid &&
                    selectionContainsAllChildren(state.selectionManager.selectedKeys, node, col)
                const someChildrenSelected =
                    !allChildrenSelected &&
                    isValid &&
                    selectionContainsSomeChildren(state.selectionManager.selectedKeys, node, col)

                return (
                    <Styled.TreeItemContainer data-hidden={hidden ? true : undefined}>
                        {selectionBehavior === 'toggle' && selectionMode !== 'none' && (
                            <Styled.Checkbox
                                slot="selection"
                                isIndeterminate={isMultiple && someChildrenSelected === true}
                                isSelected={isMultiple ? allChildrenSelected === true : undefined}
                            />
                        )}
                        {children}
                        {hasChildItems && (
                            <Styled.ExpandButton slot="chevron">
                                {stateContext?.renderExpandIcon ? (
                                    stateContext.renderExpandIcon(isExpanded)
                                ) : (
                                    <ExpandIcon isExpanded={isExpanded} />
                                )}
                            </Styled.ExpandButton>
                        )}
                    </Styled.TreeItemContainer>
                )
            }}
        </Styled.TreeItemContent>
    )
}
